import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Enhanced error logging for TDZ detection
    const errorMessage = error?.message || error?.toString() || 'Unknown error';
    const errorString = error?.toString() || '';
    const fullErrorText = `${errorMessage} ${errorString}`;
    
    const isTDZError = errorMessage.includes('Cannot access uninitialized variable') || 
                       errorMessage.includes('TDZ') ||
                       errorMessage.includes('before initialization') ||
                       fullErrorText.includes('uninitialized');
    
    // Try multiple patterns to extract variable name
    let variableName = null;
    let variableDetails = null;
    
    if (isTDZError) {
      // Check for detailed TDZ error info from ArtistWall
      if (window.lastTDZError) {
        variableName = window.lastTDZError.variables?.[0] || window.lastTDZError.variableName || null;
        variableDetails = window.lastTDZError;
      } else if (window.lastLoginTDZError) {
        variableName = window.lastLoginTDZError.variables?.[0] || null;
        variableDetails = window.lastLoginTDZError;
      }
      
      // Pattern 1: "Cannot access uninitialized variable 'variableName'"
      if (!variableName) {
        variableName = errorMessage.match(/variable ['"]([^'"]+)['"]/i)?.[1] ||
                       // Pattern 2: "variableName is not defined" or "variableName before initialization"
                       errorMessage.match(/([a-zA-Z_$][a-zA-Z0-9_$]*)\s+(?:is\s+not\s+defined|before\s+initialization)/i)?.[1] ||
                       // Pattern 3: Check window.lastTDZError from global handler
                       (window.lastTDZError && window.lastTDZError.variable) ||
                       // Pattern 4: Extract from stack trace
                       error?.stack?.match(/at\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/)?.[1] ||
                       'UNKNOWN_VARIABLE';
      }
      
      console.error('🔍 TDZ Error - Uninitialized variable detected:', variableName);
      console.error('📋 Full TDZ error details:', {
        variable: variableName,
        variableDetails: variableDetails,
        errorMessage: errorMessage,
        errorString: errorString,
        fullErrorText: fullErrorText,
        stack: error?.stack,
        componentStack: errorInfo?.componentStack,
        globalTDZError: window.lastTDZError,
        loginTDZError: window.lastLoginTDZError,
        timestamp: new Date().toISOString()
      });
    }
    
    console.error('🚨 ErrorBoundary caught an error:', {
      error: error,
      errorMessage: errorMessage,
      isTDZError: isTDZError,
      uninitializedVariable: variableName,
      errorInfo: errorInfo,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      timestamp: new Date().toISOString()
    });
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
      isTDZError: isTDZError,
      uninitializedVariable: variableName
    });
    
    // You can also log the error to an error reporting service here
  }

  handleReload = () => {
    // Clear all caches and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(reg => reg.unregister());
      });
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          backgroundColor: '#f5f5f5',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <h1 style={{ color: '#d32f2f', marginBottom: '20px' }}>
              ⚠️ Something went wrong
            </h1>
            <p style={{ color: '#666', marginBottom: '20px' }}>
              The app encountered an error. Please try reloading the page.
            </p>
            {this.state.error && (
              <details style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '12px',
                maxHeight: '300px',
                overflow: 'auto'
              }} open={this.state.isTDZError}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px', color: this.state.isTDZError ? '#d32f2f' : '#666' }}>
                  Error Details (Click to expand)
                  {this.state.isTDZError && ' - TDZ Error Detected!'}
                </summary>
                {this.state.isTDZError && this.state.uninitializedVariable && (
                  <div style={{ 
                    padding: '10px', 
                    backgroundColor: '#ffebee', 
                    borderRadius: '4px', 
                    marginBottom: '10px',
                    border: '1px solid #ef5350'
                  }}>
                    <strong style={{ color: '#d32f2f' }}>⚠️ Uninitialized Variable:</strong>
                    <code style={{ display: 'block', marginTop: '5px', color: '#c62828' }}>
                      {this.state.uninitializedVariable}
                    </code>
                    <p style={{ marginTop: '5px', fontSize: '11px', color: '#666' }}>
                      This variable is being accessed before it's initialized. Check the component code for variable declaration order.
                    </p>
                    {(window.lastTDZError || window.lastLoginTDZError) && (
                      <details style={{ marginTop: '10px', fontSize: '10px' }}>
                        <summary style={{ cursor: 'pointer', color: '#d32f2f' }}>View Detailed Variable Checks</summary>
                        <pre style={{ 
                          marginTop: '5px', 
                          padding: '5px', 
                          backgroundColor: '#fff', 
                          borderRadius: '4px',
                          fontSize: '9px',
                          overflow: 'auto',
                          maxHeight: '200px'
                        }}>
                          {JSON.stringify(window.lastTDZError || window.lastLoginTDZError, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            <button
              onClick={this.handleReload}
              style={{
                marginTop: '20px',
                padding: '12px 24px',
                backgroundColor: '#FFC107',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;


































